import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TrendingUp, Landmark, Globe, BarChart3, Shield, Target } from "lucide-react";

const TANUJ_IMG = "https://customer-assets.emergentagent.com/job_incred-wealth-sg/artifacts/ruk4jc4c_Tanuj%20Khosla.jpg";
const RONIE_IMG = "https://customer-assets.emergentagent.com/job_incred-wealth-sg/artifacts/g7cio251_Ronie%20Ganguly.jpg";
const SAURABH_IMG = "https://customer-assets.emergentagent.com/job_incred-wealth-sg/artifacts/n2mrkw2e_Saurabh-Jhalaria.jpg";
const GIRISH_IMG = "https://customer-assets.emergentagent.com/job_incred-wealth-sg/artifacts/3vxi29xz_Girish%20Rathod.jpg";

const creditFeatures = [
  { icon: Globe, title: "Emerging Market Focus", desc: "Targeting attractive credit opportunities across emerging market economies with a strong Asian bias" },
  { icon: Landmark, title: "Institutional Grade", desc: "Rigorous credit analysis and due diligence on each position" },
  { icon: BarChart3, title: "Yield Enhancement", desc: "Seeking superior risk-adjusted yield through careful credit selection" },
  { icon: Shield, title: "Portfolio Construction", desc: "Diversified across geographies, sectors, and credit quality spectrum" },
];

const Strategies = () => {
  return (
    <section id="strategies" data-testid="strategies-section" className="py-24 sm:py-32 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div className="mb-16">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#4A6670] mb-4 font-body" data-testid="strategies-overline">
            What We Offer
          </p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 font-heading" data-testid="strategies-title">
            Our Strategies
          </h2>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="public-equity" className="w-full">
          <TabsList className="bg-white border border-slate-200 rounded-sm p-1 h-auto w-full sm:w-auto inline-flex flex-wrap" data-testid="strategies-tabs-list">
            <TabsTrigger
              value="public-equity"
              className="rounded-sm px-6 py-3 text-sm font-medium font-heading data-[state=active]:bg-[#E87722] data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200"
              data-testid="tab-public-equity"
            >
              Public Equity
            </TabsTrigger>
            <TabsTrigger
              value="private-equity"
              className="rounded-sm px-6 py-3 text-sm font-medium font-heading data-[state=active]:bg-[#E87722] data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200"
              data-testid="tab-private-equity"
            >
              Private Equity
            </TabsTrigger>
            <TabsTrigger
              value="credit"
              className="rounded-sm px-6 py-3 text-sm font-medium font-heading data-[state=active]:bg-[#E87722] data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200"
              data-testid="tab-credit"
            >
              Credit
            </TabsTrigger>
          </TabsList>

          {/* Public Equity Tab */}
          <TabsContent value="public-equity" className="mt-8" data-testid="tab-content-public-equity">
            <div className="bg-white border border-slate-200 rounded-sm p-8 sm:p-12">
              <h3 className="text-2xl font-medium text-slate-900 font-heading mb-4" data-testid="geof-title">
                GEOF
              </h3>
              <div className="w-12 h-0.5 bg-[#E87722] mb-6" />
              <p className="text-sm leading-relaxed text-slate-600 font-body" data-testid="geof-desc">
                InCred Global Equity Opportunities Fund (GEOF) employs an Equity Long Short strategy with a Developed Markets focus. It uses a well tested quantemental process combining quantitative methods for narrowing down large universe of stocks and then applies fundamental analysis for discretionary stock selection. It has delivered market beating performance with low downside volatility especially during periods of market stress. The fund is highly diversified and offers weekly liquidity.
              </p>
            </div>
          </TabsContent>

          {/* Private Equity Tab */}
          <TabsContent value="private-equity" className="mt-8" data-testid="tab-content-private-equity">
            <div className="bg-white border border-slate-200 rounded-sm p-8 sm:p-12">
              <h3 className="text-2xl font-medium text-slate-900 font-heading mb-4" data-testid="pe-title">
                Private Equity
              </h3>
              <div className="w-12 h-0.5 bg-[#E87722] mb-6" />
              <p className="text-base leading-relaxed text-slate-400 font-body italic" data-testid="pe-desc">
                To be added
              </p>
            </div>
          </TabsContent>

          {/* Credit Tab */}
          <TabsContent value="credit" className="mt-8" data-testid="tab-content-credit">
            <div className="bg-white border border-slate-200 rounded-sm p-8 sm:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-5">
                  <h3 className="text-2xl font-medium text-slate-900 font-heading mb-4" data-testid="emcredit-title">
                    InCred EM Multi-Strategy Credit Fund
                  </h3>
                  <div className="w-12 h-0.5 bg-[#E87722] mb-6" />
                  <p className="text-sm leading-relaxed text-slate-600 font-body mb-4" data-testid="emcredit-desc-1">
                    InCred EM Multi-Strategy Credit Fund (IEMCOF) is an emerging market credit fund with a strong Asian bias, principally investing in performing debt obligations of EM and Asian borrowers.
                  </p>
                  <p className="text-sm leading-relaxed text-slate-600 font-body" data-testid="emcredit-desc-2">
                    The Investment Manager constructs a portfolio anchored in public market credit — spanning investment grade and high yield bonds — traded actively from both long and short sides to capture alpha from outright mispricings and relative value dislocations. This core strategy is complemented by a sleeve of performing private credit loans to high-quality borrowers, providing uncorrelated return streams and enhancing overall portfolio resilience.
                  </p>

                  {/* Key metrics */}
                  <div className="grid grid-cols-2 gap-4 mt-8">
                    <div className="border-l-2 border-[#E87722] pl-4">
                      <p className="text-2xl font-medium text-slate-900 font-heading" data-testid="emcredit-metric-1">Credit</p>
                      <p className="text-xs text-slate-500 font-body">Asset Class</p>
                    </div>
                    <div className="border-l-2 border-[#4A6670] pl-4">
                      <p className="text-2xl font-medium text-slate-900 font-heading" data-testid="emcredit-metric-2">EM Focus</p>
                      <p className="text-xs text-slate-500 font-body">Geographic Mandate (Asia Biased)</p>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    {creditFeatures.map((feat, idx) => (
                      <div
                        key={idx}
                        className="group p-6 bg-[#F8FAFC] border border-slate-100 rounded-sm hover:border-[#4A6670] transition-all duration-300"
                        data-testid={`emcredit-feature-${idx}`}
                      >
                        <div className="w-10 h-10 flex items-center justify-center bg-white rounded-sm mb-4 shadow-sm">
                          <feat.icon className="h-5 w-5 text-[#E87722]" />
                        </div>
                        <h4 className="text-sm font-semibold text-slate-900 font-heading mb-2">{feat.title}</h4>
                        <p className="text-xs text-slate-500 leading-relaxed font-body">{feat.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Portfolio Manager */}
              <div className="border-t border-slate-200 mt-10 pt-10">
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#4A6670] mb-6 font-body">Portfolio Manager</p>
                <div className="flex flex-col sm:flex-row gap-6 items-start">
                  <img
                    src={TANUJ_IMG}
                    alt="Tanuj Khosla, CFA"
                    className="w-24 h-24 rounded-sm object-cover object-top flex-shrink-0"
                    data-testid="credit-pm-image"
                  />
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 font-heading" data-testid="credit-pm-name">
                      Tanuj Khosla, CFA
                    </h4>
                    <p className="text-xs font-medium text-[#E87722] mt-1 uppercase tracking-wide font-body" data-testid="credit-pm-title">
                      Managing Director | Portfolio Manager | Head of Multi-Strategy Credit, InCred Global Asset Management
                    </p>
                    <p className="text-xs text-slate-500 mt-3 leading-relaxed font-body" data-testid="credit-pm-bio">
                      Tanuj Khosla is a multi-strategy credit investor with 14+ years of buy-side experience across APAC and the Middle East, spanning public and private markets. He has deployed capital across investment grade, high yield, distressed and special situations — in both hard and local currency — across 20+ jurisdictions, navigating complex cross-border capital structures in fragmented and structurally inefficient markets. His experience includes building and running credit books from inception, managing risk through cycles, and generating alpha in both emerging and developed credit ecosystems. Previously he ran EM Credit Proprietary Trading book at Louis Dreyfus Company. Prior to that he managed the Asia Credit pod inside the flagship multi-strategy fund of Dymon Asia Capital. He was ranked No. 1 in The Asset's "Most Astute Investors in Asian G3 Bonds" (2025), with prior recognition in 2022 and 2016. He holds both the CFA and CAIA Charters and is an MBA (Finance & Strategy) from Nanyang Business School.
                    </p>
                  </div>
                </div>
              </div>

              {/* Head of Credit Research */}
              <div className="border-t border-slate-200 mt-10 pt-10">
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#4A6670] mb-6 font-body" data-testid="credit-hor-label">Head of Credit Research</p>
                <div className="flex flex-col sm:flex-row gap-6 items-start" data-testid="credit-hor">
                  <img
                    src={GIRISH_IMG}
                    alt="Girish Rathor"
                    className="w-24 h-24 rounded-sm object-cover object-top flex-shrink-0"
                    data-testid="credit-hor-image"
                  />
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 font-heading" data-testid="credit-hor-name">
                      Girish Rathor
                    </h4>
                    <p className="text-xs font-medium text-[#E87722] mt-1 uppercase tracking-wide font-body" data-testid="credit-hor-title">
                      Head of Credit Research
                    </p>
                    <ul className="text-xs text-slate-500 mt-3 leading-relaxed font-body space-y-1.5 list-disc pl-4" data-testid="credit-hor-bio">
                      <li>Girish is a highly experienced professional with a 17-year career anchored by 14 years of dedicated focus on credit investing and an additional three years in credit ratings</li>
                      <li>He has significant experience of investments across multiple credit cycles, with a specialized focus on performing credit and special situations</li>
                      <li>His investment background includes extensive experience across the entire capital structure. This expertise is applied across a highly diversified, multi-sector portfolio.</li>
                      <li>His prior stints include Lonestar Fund's NBFC, Edelweiss Alternatives, and ICRA Limited (Moody's India)</li>
                      <li>Girish brings strong analytical fundamentals to his approach, holding a Post Graduate Diploma in Management from the Indian Institute of Management, Kozhikode, and a Bachelor's in Technology from the Indian Institute of Technology, Bombay</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Chief Risk Officer */}
              <div className="border-t border-slate-200 mt-10 pt-10">
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#4A6670] mb-6 font-body" data-testid="credit-cro-label">Chief Risk Officer</p>
                <div className="flex flex-col sm:flex-row gap-6 items-start" data-testid="credit-cro">
                  <div className="w-24 h-24 rounded-sm bg-gradient-to-br from-[#4A6670] to-[#2E444D] flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl font-heading font-medium text-white/90 tracking-wider">PO</span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 font-heading" data-testid="credit-cro-name">
                      Philipp Orgler
                    </h4>
                    <p className="text-xs font-medium text-[#E87722] mt-1 uppercase tracking-wide font-body" data-testid="credit-cro-title">
                      Quantitative & Risk
                    </p>
                    <ul className="text-xs text-slate-500 mt-3 leading-relaxed font-body space-y-1.5 list-disc pl-4" data-testid="credit-cro-bio">
                      <li>24+ years experience in Financial Services across Deutsche Bank, Barclays Investment Bank & Mizuho</li>
                      <li>Previously MD & Head Structuring, Japan for Barclays Investment Bank & Head of Structured MTNs & Repacks for Mizuho</li>
                      <li>Was Director Interest rate structuring at Deutsche Bank and a Senior Strategist at Rokos Capital Management</li>
                      <li>Post Graduate research in Mathematical Physics from the University of Tokyo, MS in Theoretical Physics from Karl-Franzens-Universit&auml;t Graz & MSc Business Analytics from Imperial College, London</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Board of Advisors */}
              <div className="border-t border-slate-200 mt-10 pt-10">
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#4A6670] mb-6 font-body" data-testid="credit-boa-label">Board of Advisors</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  {/* Ronie Ganguly */}
                  <div className="flex flex-col sm:flex-row gap-5 items-start" data-testid="credit-advisor-0">
                    <img
                      src={RONIE_IMG}
                      alt="Ronie Ganguly"
                      className="w-24 h-24 rounded-sm object-cover object-top flex-shrink-0"
                      data-testid="credit-advisor-image-0"
                    />
                    <div>
                      <h4 className="text-lg font-semibold text-slate-900 font-heading" data-testid="credit-advisor-name-0">
                        Ronie Ganguly
                      </h4>
                      <p className="text-xs text-slate-400 mt-2 italic font-body" data-testid="credit-advisor-bio-0">
                        Bio to be added
                      </p>
                    </div>
                  </div>
                  {/* Saurabh Jhalaria */}
                  <div className="flex flex-col sm:flex-row gap-5 items-start" data-testid="credit-advisor-1">
                    <img
                      src={SAURABH_IMG}
                      alt="Saurabh Jhalaria"
                      className="w-24 h-24 rounded-sm object-cover object-top flex-shrink-0"
                      data-testid="credit-advisor-image-1"
                    />
                    <div>
                      <h4 className="text-lg font-semibold text-slate-900 font-heading" data-testid="credit-advisor-name-1">
                        Saurabh Jhalaria
                      </h4>
                      <p className="text-xs font-medium text-[#E87722] mt-1 uppercase tracking-wide font-body" data-testid="credit-advisor-title-1">
                        CIO, Alternative Credit Strategies, InCred Asset Management
                      </p>
                      <p className="text-xs text-slate-500 mt-2 leading-relaxed font-body" data-testid="credit-advisor-bio-1">
                        Saurabh has 20+ years of experience across the credit and financing business in Asia and has been part of the founding team at InCred Group since 2016. He is the CIO for alternative credit strategies for InCred Capital and also looks at the SME lending business at InCred Finance, the NBFC arm. Prior to joining InCred, Saurabh was a Managing Director at Deutsche Bank leading the private financing and performing credit business for India and South East Asia. He holds a PGDM from IIM Bangalore, is a CFA charterholder and has an undergraduate in commerce and accounting from St Xaviers College, Kolkata.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default Strategies;
